import { useMemo, useState } from 'react';
import { useLandmarkStore } from '../state/landmarks';
import {
  ANTWERP_CATEGORIES,
  ANTWERP_CATEGORY_COLOR,
  ANTWERP_CATEGORY_LABEL,
  type AntwerpLandmark,
  type AntwerpLandmarkCategory,
} from '../types/antwerp-landmark';

interface CategoryLegendProps {
  antwerpLandmarks: AntwerpLandmark[];
}

/**
 * Bottom-right collapsible legend for the Historical Antwerp pin layer.
 * Renders only when the layer itself is on. Click any row to hide that
 * category's pins; counts reflect the dataset, not what's currently
 * filtered. Show-all / Hide-all in the footer for quick isolation.
 */
export function CategoryLegend({ antwerpLandmarks }: CategoryLegendProps) {
  const antwerpHistoricalVisible = useLandmarkStore((s) => s.antwerpHistoricalVisible);
  const hidden = useLandmarkStore((s) => s.antwerpHiddenCategories);
  const toggleCategory = useLandmarkStore((s) => s.toggleAntwerpCategory);
  const showAll = useLandmarkStore((s) => s.showAllAntwerpCategories);
  const hideAll = useLandmarkStore((s) => s.hideAllAntwerpCategories);
  const [expanded, setExpanded] = useState(true);

  const counts = useMemo(() => {
    const map = new Map<AntwerpLandmarkCategory, number>();
    for (const cat of ANTWERP_CATEGORIES) map.set(cat, 0);
    for (const al of antwerpLandmarks) {
      map.set(al.category, (map.get(al.category) ?? 0) + 1);
    }
    return map;
  }, [antwerpLandmarks]);

  // Show only categories that actually appear in the dataset, sorted by population desc.
  const rows = useMemo(() => {
    return ANTWERP_CATEGORIES.filter((cat) => (counts.get(cat) ?? 0) > 0).sort(
      (a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0),
    );
  }, [counts]);

  if (!antwerpHistoricalVisible) return null;

  const visibleCount = rows.filter((cat) => !hidden.has(cat)).length;
  const totalCount = rows.length;

  return (
    <div
      data-testid="category-legend"
      style={{
        position: 'absolute',
        bottom: 'var(--space-12)',
        right: 'var(--space-3)',
        width: expanded ? 'min(260px, calc(100vw - var(--space-6)))' : 'auto',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        zIndex: 15,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        aria-controls="category-legend-body"
        data-testid="category-legend-toggle"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          width: '100%',
          padding: '8px 12px',
          background: 'transparent',
          color: 'var(--text)',
          fontSize: '0.85rem',
          fontWeight: 600,
          textAlign: 'left',
        }}
      >
        <svg
          width={16}
          height={16}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="6" cy="6" r="2" />
          <circle cx="18" cy="6" r="2" />
          <circle cx="6" cy="18" r="2" />
          <circle cx="18" cy="18" r="2" />
        </svg>
        <span>Categories</span>
        <span
          data-testid="category-legend-count"
          style={{
            marginLeft: 'auto',
            fontSize: '0.7rem',
            padding: '1px 8px',
            borderRadius: 'var(--radius-sm)',
            background: visibleCount === totalCount ? 'var(--accent)' : 'var(--surface)',
            color: visibleCount === totalCount ? '#1a1410' : 'var(--text-muted)',
            border: visibleCount === totalCount ? 'none' : '1px solid var(--border)',
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {visibleCount}/{totalCount}
        </span>
        <svg
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
          style={{
            marginLeft: 'var(--space-1)',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 150ms ease',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {expanded ? (
        <div
          id="category-legend-body"
          data-testid="category-legend-body"
          style={{
            borderTop: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: 'var(--space-2) var(--space-2)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'min(60vh, 480px)',
              overflowY: 'auto',
            }}
          >
            {rows.map((cat) => (
              <CategoryRow
                key={cat}
                category={cat}
                count={counts.get(cat) ?? 0}
                hidden={hidden.has(cat)}
                onToggle={() => toggleCategory(cat)}
              />
            ))}
          </div>
          <div
            style={{
              borderTop: '1px solid var(--border)',
              display: 'flex',
              gap: '1px',
              background: 'var(--border)',
            }}
          >
            <FooterButton
              testId="category-legend-show-all"
              disabled={visibleCount === totalCount}
              onClick={showAll}
            >
              Show all
            </FooterButton>
            <FooterButton
              testId="category-legend-hide-all"
              disabled={visibleCount === 0}
              onClick={hideAll}
            >
              Hide all
            </FooterButton>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CategoryRow({
  category,
  count,
  hidden,
  onToggle,
}: {
  category: AntwerpLandmarkCategory;
  count: number;
  hidden: boolean;
  onToggle: () => void;
}) {
  const color = ANTWERP_CATEGORY_COLOR[category];
  const label = ANTWERP_CATEGORY_LABEL[category];
  return (
    <button
      type="button"
      data-testid={`category-row-${category}`}
      data-hidden={hidden ? 'true' : 'false'}
      onClick={onToggle}
      aria-pressed={!hidden}
      aria-label={`${hidden ? 'Show' : 'Hide'} ${label} (${count})`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        width: '100%',
        padding: '6px 8px',
        background: 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        textAlign: 'left',
        opacity: hidden ? 0.45 : 1,
        transition: 'opacity 120ms ease, background 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--surface-hover, var(--surface))';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          background: color,
          border: '2px solid var(--surface-raised)',
          boxShadow: hidden ? 'none' : '0 0 0 1px ' + color,
          filter: hidden ? 'saturate(0.2)' : 'none',
          flexShrink: 0,
        }}
      />
      <span
        style={{
          flex: 1,
          fontSize: '0.82rem',
          color: 'var(--text)',
          fontFamily: 'var(--font-body)',
          textDecoration: hidden ? 'line-through' : 'none',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: '0.7rem',
          color: 'var(--text-muted)',
          fontVariantNumeric: 'tabular-nums',
          minWidth: 22,
          textAlign: 'right',
        }}
      >
        {count}
      </span>
      {hidden ? (
        <svg
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
          style={{ color: 'var(--text-muted)', flexShrink: 0 }}
        >
          <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
          <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
          <line x1="2" y1="2" x2="22" y2="22" />
        </svg>
      ) : (
        <svg
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
          style={{ color: 'var(--text)', flexShrink: 0 }}
        >
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  );
}

function FooterButton({
  children,
  onClick,
  disabled,
  testId,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        flex: 1,
        padding: '8px 10px',
        background: 'var(--surface-raised)',
        color: disabled ? 'var(--text-muted)' : 'var(--text)',
        border: 'none',
        fontSize: '0.75rem',
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {children}
    </button>
  );
}
